/**
 * AdminIntro — персональный welcome-интро для Админа/Суперадмина (per-user).
 *
 * Закрывает «слепое пятно» онбординга: приглашённый в зрелую оргу админ
 * наследует «всё готово» (setup_completed + данные орги) и раньше падал сразу
 * на боевые инсайты. Org-онбординг (OnboardingChecklist/cold-advisor) — про
 * настройку тенанта; этот блок — про знакомство КОНКРЕТНОГО человека с экраном.
 *
 * Гейт показа — на стороне DashboardInsights (роль, setup_completed, !admin_onboarded,
 * state !== 'cold'). Закрытие фиксируется per-user: POST /api/onboarding/admin-seen.
 *
 * Все строки — через t() (i18n). PostHog: показ + клик «Понятно».
 */
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { track } from '../analytics';
import { BRAND_NAME } from '../branding';

const PATHS = {
  sparkles: 'M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9zM18 14l1 2.4 2.4 1-2.4 1-1 2.4-1-2.4-2.4-1 2.4-1z',
  grid:     'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  bolt:     'M13 2L4 14h7l-1 8 9-12h-7z',
  user:     'M12 12a4 4 0 100-8 4 4 0 000 8zM4 21a8 8 0 0116 0',
  check:    'M5 13l4 4L19 7',
};

function Ico({ name, size = 18 }) {
  const d = PATHS[name] || PATHS.grid;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {d.split('M').filter(Boolean).map((seg, i) => <path key={i} d={'M' + seg} />)}
    </svg>
  );
}

function Point({ ico, title, sub }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
      <div style={{
        width: 34, height: 34, flexShrink: 0, borderRadius: '50%',
        background: 'var(--bg3)', color: 'var(--accent)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}><Ico name={ico} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 1, lineHeight: 1.35 }}>{sub}</div>
      </div>
    </div>
  );
}

// Action-карточка «заполни профиль»: акцентная (CTA) пока не сделано, done — серая ✓.
function ProfileAction({ done, title, sub, cta, onClick }) {
  return (
    <div
      data-ph-id="admin-intro-profile"
      style={{
        display: 'flex', alignItems: 'center', gap: 12,
        background: done ? 'var(--bg2)' : 'var(--bg3)',
        border: `1px solid ${done ? 'var(--border)' : 'var(--accent)'}`,
        borderRadius: 'var(--radius-lg)', padding: '12px 14px',
        opacity: done ? 0.7 : 1,
      }}
    >
      <div style={{
        width: 34, height: 34, flexShrink: 0, borderRadius: '50%',
        background: done ? 'var(--green, #0f6e56)' : 'var(--accent)', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}><Ico name={done ? 'check' : 'user'} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)', textDecoration: done ? 'line-through' : 'none' }}>{title}</div>
        {!done && <div style={{ fontSize: 12.5, color: 'var(--text2)', marginTop: 1, lineHeight: 1.35 }}>{sub}</div>}
      </div>
      {!done && (
        <button className="btn btn-secondary btn-sm" onClick={onClick}
          style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{cta}</button>
      )}
    </div>
  );
}

export default function AdminIntro({ name, profileDone = false, onProfile, onDone }) {
  const { t } = useTranslation();

  const dismiss = () => {
    track('admin_intro_dismissed', { profile_done: profileDone });
    api.post('/onboarding/admin-seen').catch(() => {}); // best-effort, не блокируем UI
    onDone?.();
  };

  const goProfile = () => {
    track('admin_intro_profile_clicked');
    onProfile?.();
  };

  return (
    <div
      data-ph-id="admin-intro"
      style={{
        background: 'rgba(79,142,247,0.06)', border: '1px solid rgba(79,142,247,0.3)',
        borderRadius: 'var(--radius-lg)', padding: '18px 18px 16px', marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12 }}>
        <span style={{ color: 'var(--accent)', flexShrink: 0 }}><Ico name="sparkles" size={20} /></span>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
          {name ? t('adminIntro.titleName', { name, brand: BRAND_NAME }) : t('adminIntro.title', { brand: BRAND_NAME })}
        </div>
      </div>

      <div style={{ fontSize: 13.5, color: 'var(--text2)', lineHeight: 1.45, marginBottom: 14 }}>
        {t('adminIntro.lead')}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 14 }}>
        <Point ico="sparkles" title={t('adminIntro.point1Title')} sub={t('adminIntro.point1Sub')} />
        <Point ico="grid"     title={t('adminIntro.point2Title')} sub={t('adminIntro.point2Sub')} />
        <Point ico="bolt"     title={t('adminIntro.point3Title')} sub={t('adminIntro.point3Sub')} />
      </div>

      {/* Единственный Action флоу: фото + телефон в профиле */}
      <div style={{ marginBottom: 16 }}>
        <ProfileAction
          done={profileDone}
          title={t('adminIntro.profileTitle')}
          sub={t('adminIntro.profileSub')}
          cta={t('adminIntro.profileCta')}
          onClick={goProfile}
        />
      </div>

      <button
        className="btn btn-primary"
        onClick={dismiss}
        data-ph-id="admin-intro-dismiss"
        style={{ width: '100%' }}
      >
        {t('adminIntro.cta')}
      </button>
    </div>
  );
}
